import React from 'react';

import { DeleteIcon } from 'src/app/components/icons/delete-icon';
import { useSaveWorkflowCampaignMutation } from 'src/app/queries/marketing/upsert-workflow-campaign';
import { useNotificationActions } from 'src/app/state/notifications';

import { useWorkflowBuilderContext } from '../../../wizard/builder-context';
import {
  getWorkflowEdgesFromReactFlowEdges,
  getWorkflowNodesFromReactFlowNodes,
  getSaveWorkflowCampaignPayload,
} from '../../../wizard/helpers';

import { NewNodeStats } from './new-node-stats';
import { NodeControlsContainer } from './styles';

import type { ActionType, NodeType } from '../models';
import type { WorkflowStepStats } from 'src/app/queries/marketing/get-workflow-execution-stats';
import type { ApiErrorResponse } from 'src/app/queries/marketing/upsert-campaign';

export function NodeActions({
  stats,
  nodeId,
  showDeleteButton = true,
  deliverabilityStats,
  nodeType,
  isDeliverabilityMetricsLoading,
  showAnalytics = true,
}: {
  deliverabilityStats?: any;
  isDeliverabilityMetricsLoading?: boolean;
  nodeId: string;
  nodeType: ActionType | NodeType;
  showAnalytics?: boolean;
  showDeleteButton?: boolean;
  stats?: WorkflowStepStats;
}) {
  const [builderState, dispatch] = useWorkflowBuilderContext();
  const notifications = useNotificationActions();
  const { mutate: saveWorkflowMutator } = useSaveWorkflowCampaignMutation();

  const handleOnNodesDelete = () => {
    const outboundEdges = builderState.edges.filter((edge) => edge.source === nodeId);
    const inboundEdges = builderState.edges.filter((edge) => edge.target === nodeId);

    if (outboundEdges.length > 1) {
      notifications.error({
        message: 'Cannot delete a conditional node with multiple outbound edges.  Delete one of the subtrees first.',
      });
      return;
    }

    if (outboundEdges.length === 1) {
      const nextNodeId = outboundEdges[0].target;
      // restitch the graph together
      for (const edge of inboundEdges) {
        edge.target = nextNodeId;
      }
    }

    const updatedNodes = builderState.nodes.filter((node) => node.id !== nodeId);
    const updatedEdges = builderState.edges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId);

    dispatch({ type: 'set-nodes', payload: updatedNodes });
    dispatch({ type: 'set-edges', payload: updatedEdges });
    dispatch({
      type: 'set-value',
      payload: {
        key: 'selectedNode',
        value: null,
      },
    });

    saveWorkflowMutator(
      getSaveWorkflowCampaignPayload(
        {
          Nodes: getWorkflowNodesFromReactFlowNodes(updatedNodes),
          Edges: getWorkflowEdgesFromReactFlowEdges(updatedEdges),
        },
        builderState
      ),
      {
        onError: (e) => {
          const error = e as ApiErrorResponse;
          const { data, status } = error.response;
          // a 400 error will return a user-friendly error message
          if (status === 400) {
            notifications.error({ message: data.Message });
          } else {
            notifications.error({ message: 'Error saving workflow' });
          }
        },
      }
    );
  };

  const showDelete = !builderState.isReadOnly && showDeleteButton;

  return (
    <NodeControlsContainer>
      {showDelete && (
        <DeleteIcon fill='var(--color-grayscale-black)' height='17' width='15' onClick={handleOnNodesDelete} />
      )}
      {!showDelete && <div />}
      <NewNodeStats
        deliverabilityStats={deliverabilityStats}
        isDeliverabilityMetricsLoading={isDeliverabilityMetricsLoading}
        nodeType={nodeType}
        showAnalytics={showAnalytics}
        stats={stats}
      />
    </NodeControlsContainer>
  );
}
